/* Globals GM_STATE */
import { useState, useEffect, useMemo } from 'react'
import { LoaderSpin } from './LoaderSpin'
import { OmneoService } from '../helpers/omneo.service'
import { OmneoUpdateCommsPreferences, CommunicationPreferences } from '../types'

declare global {
  interface Window {
    omneoService: OmneoService
  }
}

const communicationPreferences:CommunicationPreferences = GM_STATE.communicationPreferences

export function OmneoCommunicationPreferences() {
  const [loading, setLoading] = useState<boolean>(true)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)

  const [commsPreferences, setCommsPreferences] = useState<OmneoUpdateCommsPreferences>({
    email_promo: false,
    email_service: false,
    email_account: false,
    email_optout: false,
    sms_promo: false,
    sms_service: false,
    sms_account: false,
    sms_optout: false
  })

  useEffect(() => {
    window.omneoService.getCommsPreferences().then((data: OmneoUpdateCommsPreferences | null) => {
      if (!data) {
        setError(true)
        return
      }

      setCommsPreferences(data as OmneoUpdateCommsPreferences)
      setLoading(false)
    })
  }, [])

  const preferencesProps = useMemo(() => [
    { key: 'email', props: ['email_promo', 'email_service', 'email_account', 'email_optout'] },
    { key: 'sms', props: ['sms_promo', 'sms_service', 'sms_account', 'sms_optout'] }
  ], [])

  const onChangeHandler = (preference: string) => {
    if (isFetching) return
    setIsFetching(true)

    const newValueOfPreference = !commsPreferences[preference]
    const update: Partial<OmneoUpdateCommsPreferences> = { [preference]: newValueOfPreference }
    window.omneoService.setCommsPreferences(update).then((data: OmneoUpdateCommsPreferences | null) => {
      if (!data) {
        setError(true)
      }
      setCommsPreferences(data as OmneoUpdateCommsPreferences)
      setTimeout(()=> {setIsFetching(false)}, 1500) // Rate Limiting to prevent spamming the API - due to Omneo listening to Shopify webhooks and causing weird behaviour https://convertdigital.slack.com/archives/C04V39YVAKA/p1718689217552989?thread_ts=1718687227.400599&cid=C04V39YVAKA
    })
  }

  if (error)
    return (
      <div className="Segment">
        <div className="Segment__Content flex flex-col">
          <h2 className="h6 pb-[15px] uppercase border-b border-b-border-secondary md:pb-5">{communicationPreferences.error.title}</h2>
          <div className="flex flex-col gap-4">
            <p>{communicationPreferences.error.content}</p>
          </div>
        </div>
      </div>
    )

    return (
      <div className="Segment">
        {preferencesProps.map(({ key, props }) => (
          <div key={key} className="Segment__Content flex flex-col">
            <h2 className="h6 pb-[15px] uppercase border-b border-b-border-secondary md:pb-5">{communicationPreferences.success[`${key}_title`]}</h2>
            <div className="flex flex-col gap-4 py-[30px]">
              {loading ? (
                <div className='relative'>
                  <LoaderSpin />
                </div>
              ) : (
                props.map((prop) => {
                  const displayProp = communicationPreferences.success[prop as keyof typeof communicationPreferences.success]
                  return (
                    <label key={`${key}_consent_${prop}`} htmlFor={prop} className="flex justify-between">
                      <span className="text-sm-c" dangerouslySetInnerHTML={{ __html: displayProp }} />
                      <input
                        aria-label={prop}
                        id={prop}
                        className="switch hover:cursor-pointer"
                        type="checkbox"
                        disabled={(prop !== `${key}_optout` && commsPreferences[`${key}_optout` as keyof OmneoUpdateCommsPreferences])}
                        name={prop}
                        value={commsPreferences[prop as keyof OmneoUpdateCommsPreferences].toString()}
                        checked={commsPreferences[prop as keyof OmneoUpdateCommsPreferences]}
                        onChange={() => onChangeHandler(prop)}
                      />
                    </label>
                  )
                })
              )}
            </div>
          </div>
        ))}
      </div>
    )
}
